import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "../lib/constants";

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#050505]/85 border-b border-white/10"
                    : "bg-gradient-to-b from-black/40 to-transparent border-b border-transparent"
            }`}
        >
            <div className="container-bevel flex items-center justify-between h-[88px]">
                {/* Brand */}
                <Link
                    to="/"
                    data-testid="header-logo-link"
                    className="flex items-center gap-3 shrink-0"
                >
                    <img
                        src={LOGO_URL}
                        alt="Bevel Commercial Services"
                        className="h-14 w-14 md:h-16 md:w-16 object-contain"
                    />
                    <div className="flex flex-col leading-none">
                        <span className="font-display text-base sm:text-lg md:text-xl text-white tracking-wide">
                            BEVEL
                        </span>
                        <span className="text-[8px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.28em] text-white/55 mt-1 sm:mt-1.5">
                            Commercial Services
                        </span>
                    </div>
                </Link>

                {/* Desktop nav */}
                <nav className="hidden xl:flex items-center gap-7 whitespace-nowrap">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
                            className={({ isActive }) =>
                                `relative text-[12px] uppercase tracking-[0.2em] transition-colors py-2 ${
                                    isActive
                                        ? "text-[#F5A623]"
                                        : "text-white/70 hover:text-white"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    <span
                                        className={`absolute left-0 right-0 -bottom-0.5 h-px transition-opacity ${
                                            isActive
                                                ? "bg-[#F5A623] opacity-100"
                                                : "bg-[#F5A623] opacity-0"
                                        }`}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                {/* Right actions */}
                <div className="flex items-center gap-3 shrink-0">
                    <Link
                        to="/contact"
                        data-testid="header-cta-button"
                        className="hidden sm:inline-flex items-center justify-center bg-[#F5A623] text-black font-semibold uppercase tracking-[0.18em] text-[11px] px-5 py-3 hover:bg-[#FFB835] transition-colors whitespace-nowrap"
                    >
                        Get a Quote
                    </Link>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="header-mobile-toggle"
                        className="xl:hidden p-2 text-white"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile / tablet drawer */}
            {open && (
                <div
                    className="xl:hidden backdrop-blur-xl bg-[#050505]/95 border-t border-white/10"
                    data-testid="mobile-nav-drawer"
                >
                    <div className="container-bevel py-6 flex flex-col gap-1">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `py-3 text-sm uppercase tracking-[0.2em] border-b border-white/5 ${
                                        isActive
                                            ? "text-[#F5A623]"
                                            : "text-white/80"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="mobile-cta-button"
                            className="btn-primary mt-6 w-full"
                        >
                            Get a Quote
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
